import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock, Eye, EyeOff, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

type PasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordSchema = useMemo(() => z
    .object({
      currentPassword: z
        .string()
        .min(1, t('profile:modals.changePassword.currentPasswordRequired')),
      newPassword: z
        .string()
        .min(1, t('profile:modals.changePassword.newPasswordRequired'))
        .min(8, t('profile:modals.changePassword.minLength'))
        .regex(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          t('profile:modals.changePassword.complexity')
        ),
      confirmPassword: z.string().min(1, t('profile:modals.changePassword.confirmRequired')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('profile:modals.changePassword.mismatch'),
      path: ['confirmPassword'],
    }), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      reset();
      setError(null);
      setSuccess(false);
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: PasswordFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      // First, verify the current password by attempting to sign in
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.email) {
        setError(t('profile:modals.changePassword.userNotFound'));
        return;
      }

      // Try to sign in with current password to verify it
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: userData.user.email,
        password: data.currentPassword,
      });

      if (signInError) {
        setError(t('profile:modals.changePassword.incorrectPassword'));
        return;
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword,
      });

      if (updateError) {
        setError(updateError.message);
        return;
      }

      setSuccess(true);
    } catch {
      setError(t('profile:modals.changePassword.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    reset();
    onClose();
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={handleClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 text-pp-orange" />
              </div>
              <h2>{t('profile:modals.changePassword.title')}</h2>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            {success ? (
              // Success State
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-pp-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-pp-teal" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {t('profile:modals.changePassword.successTitle')}
                </h3>
                <p className="text-white/70 mb-4">
                  {t('profile:modals.changePassword.successMessage')}
                </p>
                <div className="bg-pp-orange/20 border border-pp-orange/30 rounded-[15px] p-4 text-sm text-white">
                  <strong>{t('common:important', { defaultValue: 'Hinweis' })}:</strong> {t('profile:modals.changePassword.securityNote')}
                </div>
              </div>
            ) : (
              // Form State
              <>
                <p className="text-sm text-white/70">
                  {t('profile:modals.changePassword.description')}
                </p>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-[10px] flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-300">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {/* Current Password */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      {t('profile:modals.changePassword.currentPassword')}
                    </label>
                    <div className="relative">
                      <input
                        {...register('currentPassword')}
                        type={showCurrentPassword ? 'text' : 'password'}
                        placeholder={t('profile:modals.changePassword.currentPasswordPlaceholder')}
                        autoComplete="current-password"
                        className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {errors.currentPassword && (
                      <p className="mt-1 text-sm text-red-400">{errors.currentPassword.message}</p>
                    )}
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      {t('profile:modals.changePassword.newPassword')}
                    </label>
                    <div className="relative">
                      <input
                        {...register('newPassword')}
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder={t('profile:modals.changePassword.newPasswordPlaceholder')}
                        autoComplete="new-password"
                        className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <p className="mt-1 text-sm text-red-400">{errors.newPassword.message}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      {t('profile:modals.changePassword.confirmPassword')}
                    </label>
                    <div className="relative">
                      <input
                        {...register('confirmPassword')}
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder={t('profile:modals.changePassword.confirmPasswordPlaceholder')}
                        autoComplete="new-password"
                        className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-400">{errors.confirmPassword.message}</p>
                    )}
                  </div>

                  {/* Password Requirements */}
                  <div className="text-xs text-white/50 space-y-1">
                    <p>{t('profile:modals.changePassword.requirements')}</p>
                    <ul className="list-disc list-inside space-y-0.5 pl-1">
                      <li>{t('profile:modals.changePassword.reqMinLength')}</li>
                      <li>{t('profile:modals.changePassword.reqUppercase')}</li>
                      <li>{t('profile:modals.changePassword.reqLowercase')}</li>
                      <li>{t('profile:modals.changePassword.reqNumber')}</li>
                    </ul>
                  </div>
                </form>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            {success ? (
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90"
              >
                {t('common:actions.close')}
              </button>
            ) : (
              <>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
                >
                  {t('common:actions.cancel')}
                </button>
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('profile:modals.changePassword.submitButton')
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
